import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment/environment';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { markHandledByInterceptor, reportHttpFailure } from '@shared/helpers/sentry.helper';
import { ImpersonationService } from '@shared/services/impersonation.service';
import { embedReenterUrl } from '@shared/helpers/embed-context.helper';
import { isPublicFormPath } from '@shared/helpers/public-form.helper';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class InterceptorService implements HttpInterceptor {
  constructor(
    private readonly _commonEnvironments: CommonEnvironmentsService,
    private readonly _router: Router,
    private readonly _impersonation: ImpersonationService
  ) {}

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let token = this._commonEnvironments.getToken();
    const isCountriesReq = request.url.indexOf('restcountries.eu') > -1;
    const isIPAddressReq = request.url.indexOf('ipinfo.io') > -1;

    const accept = {
      Accept: 'application/json'
    };

    const contentType = {
      'Content-Type': 'application/json'
    };

    if (isIPAddressReq) {
      token = environment.IP_INFO;
    }

    if (!!token && !isCountriesReq && !isIPAddressReq) {
      const authorization = {
        Authorization: `Bearer ${token}`
      };

      request =
        request.url.endsWith('/logo') ||
        request.url.endsWith('/upload') ||
        request.url.endsWith('/pictures') ||
        request.url.indexOf('/documents') > -1
          ? request.clone({
              setHeaders: {
                ...accept,
                ...authorization
              }
            })
          : request.clone({
              setHeaders: {
                ...accept,
                ...contentType,
                ...authorization
              }
            });
    } else if (isCountriesReq) {
      // Get list countries
      request = request.clone();
    } else if (isIPAddressReq) {
      const authorization = {
        Authorization: `Bearer ${token}`
      };

      request = request.clone({
        setHeaders: {
          ...accept,
          ...authorization
        }
      });
    } else {
      request = request.clone({
        setHeaders: {
          ...accept,
          ...contentType
        }
      });
    }

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>): any => {
        if (event instanceof HttpResponse) {
          // do stuff with response if you want
        }
      }),
      catchError((err: HttpErrorResponse) => {
        if (err instanceof HttpErrorResponse) {
          // 5xx and status-0 failures never reached the API's own reporting
          // and the rethrow below strips the status, so this is the one place
          // they can be captured with the method and endpoint intact.
          reportHttpFailure(err, request.method);

          // TO DO: WAITING ERROR HANDLER TO PUT BACK THE 500 CATCH
          // BY DEFAULT, API RETURNS 500. I CAUGHT THE 500 IF TOKEN IS INCORRECT
          // AND NEED TO BE REFRESHED

          // if (err.status === 401 || err.status === 500) {

          const status = err?.status;
          const isEmbedReq =
            request.url.indexOf('/embed_session') > -1 ||
            request.url.indexOf('/embed_request') > -1;

          if (status === 401) {
            const embedUrl = embedReenterUrl();
            if (embedUrl) {
              this._router.navigateByUrl(embedUrl);
              return throwError(() => err);
            }

            // A visitor on a public token-link form (e.g. a referee whose
            // email also has a recruiter account, carrying a stale
            // recruiter-token cookie) must never be bounced to /login: the
            // form itself needs no auth, the 401 comes from an incidental
            // authenticated call made with the stale token. Don't wipe
            // storage either — the form's own session state lives there.
            if (isPublicFormPath()) {
              return throwError(() => err);
            }

            // init() must read the impersonation state out of storage before
            // any cleanup wipes it. An expired support token still belongs to
            // an admin, so route them back to the admin app rather than the
            // recruiter login screen — hence the claim flag, not the
            // live-session one.
            this._impersonation.init();
            if (this._impersonation.hasImpersonationToken) {
              this._commonEnvironments.cleanStorage();
              this._impersonation.redirect(environment.ENDPOINTS.FRONT_END.ADMIN);
              return throwError(() => err);
            }

            // Clean the storage only once we've left the protected route.
            // cleanStorage() is synchronous while navigate() is not, so wiping
            // first leaves a window where a still-mounted component re-renders
            // against null @SessionStorage() values and throws during change
            // detection (e.g. `settings.is_webhook_enabled`).
            const cleanStorage = () => this._commonEnvironments.cleanStorage();
            this._router.navigate(['/login']).then(cleanStorage, cleanStorage);

            // The redirect is the handling. A route resolver (the protected
            // shell resolves /me) or a subscriber without an error handler
            // would otherwise surface the rethrown error list to Sentry as
            // one issue per detail (VITAY-FRONTEND-Y0).
            markHandledByInterceptor(err?.error?.errors);
          }

          // The API rejects every non-exempt call with this code once the
          // user's password has expired; route them to the change screen.
          // Same public-form exemption as the 401 branch above: a referee
          // is not the expired recruiter and must stay on the form.
          if (
            status === 403 &&
            err?.error?.errors?.[0]?.code === 'password_expired' &&
            !isPublicFormPath()
          ) {
            this._router.navigate(['/password']);
          }

          // A password-only session (second factor still owed) that reached
          // any other endpoint, e.g. a tab restored mid-login: the MFA screen
          // is the only place it can go (VIT-1346).
          if (
            status === 403 &&
            err?.error?.errors?.[0]?.code === 'mfa_required' &&
            !isPublicFormPath()
          ) {
            this._router.navigate(['/mfa']);
          }

          // Preserve the raw HttpErrorResponse (status intact) for embed_session and
          // embed_request requests so the embed UI's errorKeyFor(err?.status) can map it.
          if (isEmbedReq) {
            return throwError(() => err);
          }

          return throwError(err?.error?.errors || JSON.stringify(err));
        }
      })
    );
  }
}
